import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../AuthProvider";

export default function AccuracyPanel() {
  const { token } = useAuth() || {};
  const [loading, setLoading] = useState(false);

  // ✅ engine selector
  const [engine, setEngine] = useState("v1"); // v1 | v2 | both
  const [viewEngine, setViewEngine] = useState("v1"); // dipakai saat engine=both

  // tanggal utama: Trading Day (T)
  const [targetDate, setTargetDate] = useState("");
  // tanggal evaluasi close
  const [holdUntil, setHoldUntil] = useState("");

  // thresholds
  const [buyThr, setBuyThr] = useState(0.0);
  const [avoidThr, setAvoidThr] = useState(0.0);
  const [watchBand, setWatchBand] = useState(0.01);
  const [neutralThr, setNeutralThr] = useState(0.0);

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const todayISO = useMemo(() => {
    const d = new Date();
    const pad2 = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  }, []);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailRows, setDetailRows] = useState([]);
  const [detailMeta, setDetailMeta] = useState(null);
  
  const openCategory = async (category) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailRows([]);
    setDetailMeta({ category });
  
    try {
      const headers = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;
  
      const qs = new URLSearchParams();
      qs.set("target_date", targetDate || todayISO);
      qs.set("hold_until", holdUntil || targetDate || todayISO);
      qs.set("engine", engine === "both" ? pickedKey : engine); // pakai view yg sedang ditampilkan
      qs.set("signal", "BUY");
      qs.set("category", category);
  
      // samakan threshold dengan panel
      qs.set("buy_thr", String(buyThr));
      qs.set("avoid_thr", String(avoidThr));
      qs.set("watch_band", String(watchBand));
      qs.set("neutral_thr", String(neutralThr));
  
      const res = await fetch(`/api/stocks/accuracy/details?${qs.toString()}`, { headers });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || "Failed to load detail");
  
      setDetailMeta(json);
      setDetailRows(json.rows || []);
    } catch (e) {
      setDetailMeta((m) => ({ ...(m || {}), error: e.message }));
    } finally {
      setDetailLoading(false);
    }
  };
  
    
  // init default date
  useEffect(() => {
    if (!targetDate) setTargetDate(todayISO);
    if (!holdUntil) setHoldUntil(todayISO);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todayISO]);

  // helper: parse "49%" / "49.21" / 49
  const toNumberPct = (v) => {
    if (v == null) return 0;
    if (typeof v === "number") return v;
    const s = String(v).trim().replace("%", "");
    const n = Number(s);
    return Number.isFinite(n) ? n : 0;
  };

  const pillClass = (pctLike) => {
    const pct = toNumberPct(pctLike);
    if (pct >= 70) return "bg-emerald-600";
    if (pct >= 50) return "bg-yellow-600";
    return "bg-red-600";
  };

  const fetchAccuracy = async () => {
    setLoading(true);
    setError(null);

    try {
      const headers = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const qs = new URLSearchParams();
      qs.set("target_date", targetDate || todayISO);
      qs.set("hold_until", holdUntil || targetDate || todayISO);

      // ✅ include engine
      qs.set("engine", engine);

      qs.set("buy_thr", String(buyThr));
      qs.set("avoid_thr", String(avoidThr));
      qs.set("watch_band", String(watchBand));
      qs.set("neutral_thr", String(neutralThr));

      const res = await fetch(`/api/stocks/accuracy?${qs.toString()}`, { headers });
      const json = await res.json();

      if (!res.ok) {
        throw new Error(json.detail || "Failed to load accuracy data");
      }

      setData(json);

      // ✅ kalau engine=both dan viewEngine belum ada di results, set otomatis
      if (json?.results && engine === "both") {
        const keys = Object.keys(json.results || {});
        if (keys.length && !keys.includes(viewEngine)) {
          setViewEngine(keys[0]);
        }
      }
    } catch (err) {
      console.error(err);
      setError(err.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  // auto refresh ketika tanggal berubah
  useEffect(() => {
    if (!targetDate) return;
    if (!holdUntil) setHoldUntil(targetDate);
    fetchAccuracy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetDate]);

  // ✅ refetch juga ketika engine berubah (biar panel langsung update)
  useEffect(() => {
    if (!targetDate) return;
    fetchAccuracy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engine]);

  const status = data?.status;

  // ✅ pilih data engine yang ditampilkan
  const results = data?.results || {};
  const resultKeys = Object.keys(results);
  const pickedKey =
    engine === "both"
      ? (viewEngine && results[viewEngine] ? viewEngine : (resultKeys[0] || "v1"))
      : (resultKeys.includes(engine) ? engine : (resultKeys[0] || engine));

  const picked = results?.[pickedKey] || {};
  const accuracyTotal = picked?.accuracy_total || [];
  const accuracyBuyByCategory = picked?.accuracy_buy_by_category || [];
  const pickedTotalRows = picked?.total_rows ?? 0;

  return (
    <div className="h-full w-full p-3 overflow-auto text-sm bg-gray-900">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-semibold text-white">
          📊 Rekap Akurasi Rekomendasi
        </h2>

        <button
          onClick={fetchAccuracy}
          className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded"
          disabled={loading}
        >
          {loading ? "…" : "Refresh"}
        </button>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap gap-2 items-end mb-3">
        <div>
          <label className="block text-[11px] text-gray-400 mb-1">Tanggal Trading</label>
          <input
            type="date"
            value={targetDate}
            onChange={(e) => {
              const v = e.target.value;
              setTargetDate(v);
              setHoldUntil(v); // default: holdUntil ikut targetDate
            }}
            className="bg-gray-800 border border-gray-600 rounded px-2 py-1 text-xs text-gray-200"
          />
        </div>

        <div>
          <label className="block text-[11px] text-gray-400 mb-1">Hold Until</label>
          <input
            type="date"
            value={holdUntil}
            onChange={(e) => setHoldUntil(e.target.value)}
            className="bg-gray-800 border border-gray-600 rounded px-2 py-1 text-xs text-gray-200"
          />
        </div>

        {/* ✅ Engine selector */}
        <div>
          <label className="block text-[11px] text-gray-400 mb-1">Engine</label>
          <select
            value={engine}
            onChange={(e) => setEngine(e.target.value)}
            className="h-[30px] bg-gray-800 border border-gray-600 rounded px-2 text-xs text-gray-200"
          >
            <option value="v1">v1</option>
            <option value="v2">v2</option>
            <option value="both">both</option>
          </select>
        </div>

        {/* ✅ viewEngine selector ketika both */}
        {engine === "both" && (
          <div>
            <label className="block text-[11px] text-gray-400 mb-1">View</label>
            <select
              value={pickedKey}
              onChange={(e) => setViewEngine(e.target.value)}
              className="h-[30px] bg-gray-800 border border-gray-600 rounded px-2 text-xs text-gray-200"
            >
              {resultKeys.length ? (
                resultKeys.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))
              ) : (
                <>
                  <option value="v1">v1</option>
                  <option value="v2">v2</option>
                </>
              )}
            </select>
          </div>
        )}

        <button
          onClick={fetchAccuracy}
          className="px-3 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-medium rounded"
          disabled={loading}
        >
          Apply
        </button>

        {/* Advanced thresholds */}
        <details className="ml-auto w-full md:w-auto">
          <summary className="cursor-pointer text-xs text-gray-300 select-none">
            Advanced thresholds
          </summary>
          <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2">
            <Field label="BUY thr" value={buyThr} setValue={setBuyThr} step="0.001" />
            <Field label="AVOID thr" value={avoidThr} setValue={setAvoidThr} step="0.001" />
            <Field label="WATCH band" value={watchBand} setValue={setWatchBand} step="0.001" />
            <Field label="NEUTRAL thr" value={neutralThr} setValue={setNeutralThr} step="0.001" />
          </div>
        </details>
      </div>

      {/* Status info */}
      {data && (
        <div className="mb-3 p-3 rounded-lg border border-gray-700 bg-gray-800/40 text-xs text-gray-200">
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <Info label="Trading Day" value={data.target_date || "-"} />
            <Info label="Sinyal dibuat" value={data.signal_date || "-"} />
            <Info label="Dinilai pakai close" value={data.hold_until || "-"} />
            <Info label="Hold (hari trading)" value={data.hold_days ?? "-"} />
            <Info label="Engine (API)" value={data.engine || "-"} />
            <Info label="View (UI)" value={pickedKey || "-"} />
            <Info label="Rows" value={pickedTotalRows} />
            <Info
              label="Status"
              value={
                status === "READY"
                  ? "READY"
                  : status === "PENDING_CLOSE"
                  ? "PENDING CLOSE"
                  : "NO DATA"
              }
            />
          </div>

          {data.message && <div className="mt-2 text-gray-300">{data.message}</div>}
          {data.warnings?.length ? (
            <div className="mt-2 text-amber-300">
              {data.warnings.map((w, i) => (
                <div key={i}>⚠️ {w}</div>
              ))}
            </div>
          ) : null}
        </div>
      )}

      {loading && <div className="text-gray-400 animate-pulse">Loading accuracy…</div>}
      {error && <div className="text-red-400">Error: {error}</div>}

      {/* Tables */}
      {!loading && data && status === "READY" && (
        <>
          {/* === Accuracy by Signal === */}
          <div className="mb-4">
            <h3 className="text-xs text-gray-400 mb-2">
              Accuracy by Signal <span className="text-gray-500">({pickedKey})</span>
            </h3>

            {accuracyTotal.length === 0 ? (
              <div className="text-gray-400 text-xs">
                {pickedTotalRows === 0
                  ? "Tidak ada data signal untuk engine/view ini pada tanggal tersebut."
                  : "Tidak ada baris yang bisa dievaluasi (mungkin close missing atau mapping kode saham tidak match)."}
              </div>
            ) : (
              <table className="w-full text-xs border border-gray-700 rounded overflow-hidden">
                <thead className="bg-gray-800">
                  <tr>
                    <th className="px-2 py-2 text-left">Signal</th>
                    <th className="px-2 py-2 text-right">Total</th>
                    <th className="px-2 py-2 text-right">Correct</th>
                    <th className="px-2 py-2 text-center">Accuracy</th>
                  </tr>
                </thead>
                <tbody>
                  {accuracyTotal.map((r) => (
                    <tr key={r.signal} className="border-t border-gray-700">
                      <td className="px-2 py-2 font-semibold">{r.signal}</td>
                      <td className="px-2 py-2 text-right">{r.total}</td>
                      <td className="px-2 py-2 text-right">{r.correct}</td>
                      <td className="px-2 py-2 text-center">
                        <span
                          className={`px-2 py-[2px] rounded text-white text-[11px] ${pillClass(
                            r.accuracy
                          )}`}
                        >
                          {String(r.accuracy).includes("%") ? r.accuracy : `${r.accuracy}%`}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* === BUY Accuracy by Category === */}
          <div>
            <h3 className="text-xs text-gray-400 mb-2">
              BUY Accuracy by Stock Type <span className="text-gray-500">({pickedKey})</span>
            </h3>

            {accuracyBuyByCategory.length === 0 ? (
              <div className="text-gray-400 text-xs">
                {pickedTotalRows === 0
                  ? "Tidak ada data signal BUY untuk engine/view ini pada tanggal tersebut."
                  : "Tidak ada kategori BUY yang bisa dievaluasi pada periode ini."}
              </div>
            ) : (
              <table className="w-full text-xs border border-gray-700 rounded overflow-hidden">
                <thead className="bg-gray-800">
                  <tr>
                    <th className="px-2 py-2 text-left">Category</th>
                    <th className="px-2 py-2 text-right">BUY</th>
                    <th className="px-2 py-2 text-right">Valid</th>
                    <th className="px-2 py-2 text-center">Accuracy</th>
                  </tr>
                </thead>
                <tbody>
                  {accuracyBuyByCategory.map((r) => (
                    <tr key={r.category} className="border-t border-gray-700">

                      <td className="px-2 py-2">
                        <button
                          className="text-sky-300 hover:text-sky-200 underline"
                          onClick={() => openCategory(r.category)}
                        >
                          {r.category}
                        </button>
                      </td>
                      
                      <td className="px-2 py-2 text-right">{r.total_buy}</td>
                      <td className="px-2 py-2 text-right">{r.valid}</td>
                      <td className="px-2 py-2 text-center">
                        <span
                          className={`px-2 py-[2px] rounded text-white text-[11px] ${pillClass(
                            r.accuracy
                          )}`}
                        >
                          {String(r.accuracy).includes("%") ? r.accuracy : `${r.accuracy}%`}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {detailOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3">
          <div className="w-full max-w-4xl rounded-xl border border-gray-700 bg-gray-900 shadow-xl">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
              <div className="text-sm text-white font-semibold">
                Detail BUY - {detailMeta?.category} ({detailMeta?.engine})
              </div>
              <button
                className="text-gray-300 hover:text-white"
                onClick={() => setDetailOpen(false)}
              >
                ✕
              </button>
            </div>
      
            <div className="p-4">
              {detailLoading ? (
                <div className="text-gray-400 text-sm">Loading…</div>
              ) : detailMeta?.error ? (
                <div className="text-red-400 text-sm">Error: {detailMeta.error}</div>
              ) : detailRows.length === 0 ? (
                <div className="text-gray-400 text-sm">Tidak ada data.</div>
              ) : (
                <div className="overflow-auto max-h-[70vh]">
                  <table className="w-full text-xs border border-gray-700 rounded overflow-hidden">
                    <thead className="bg-gray-800">
                      <tr>
                        <th className="px-2 py-2 text-left">Kode</th>
                        <th className="px-2 py-2 text-right">Suggest%</th>
                        <th className="px-2 py-2 text-right">Close H-1</th>
                        <th className="px-2 py-2 text-right">Close T</th>
                        <th className="px-2 py-2 text-right">Return%</th>
                        <th className="px-2 py-2 text-center">Valid</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailRows.map((x) => (
                        <tr key={x.kode_saham} className="border-t border-gray-700">
                          <td className="px-2 py-2 font-semibold">{x.kode_saham}</td>
                          <td className="px-2 py-2 text-right">{x.suggest_pct ?? "-"}</td>
                          <td className="px-2 py-2 text-right">{x.close_signal_date ?? "-"}</td>
                          <td className="px-2 py-2 text-right">{x.close_hold_until ?? "-"}</td>
                          <td className="px-2 py-2 text-right">
                            {x.return_pct != null ? `${x.return_pct}%` : "-"}
                          </td>
                          <td className="px-2 py-2 text-center">
                            <span className={`px-2 py-[2px] rounded text-white text-[11px] ${x.is_valid ? "bg-emerald-600" : "bg-red-600"}`}>
                              {x.is_valid ? "YES" : "NO"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
    </div>
  );
}

// small helpers
function Info({ label, value }) {
  return (
    <div>
      <span className="text-gray-400">{label}:</span>{" "}
      <span className="font-semibold text-gray-100">{value}</span>
    </div>
  );
}

function Field({ label, value, setValue, step = "0.001" }) {
  return (
    <div>
      <label className="block text-[11px] text-gray-400 mb-1">{label}</label>
      <input
        type="number"
        step={step}
        value={value}
        onChange={(e) => setValue(Number(e.target.value))}
        className="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-xs text-gray-200"
      />
    </div>
  );
}
